import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Topbar } from "../Topbar";
import { Footer } from "../Footer";
import { Hero } from "../Hero";
import { Section } from "../Section";
import { CTABlock } from "../CTABlock";
import { RevealOnScroll } from "../RevealOnScroll";
import { pathMap, SITE_URL, type Locale } from "@/lib/locale";
import {
  GLOSSARY_CATEGORY_LABELS,
  groupByFirstLetter,
  listGlossary,
  type GlossaryCategory,
  type GlossaryEntry,
} from "@/lib/glossary";

type Props = { locale: Locale };

const ALL_CATEGORIES: GlossaryCategory[] = [
  "seo-ai-seo",
  "performance-marketing",
  "stack-technical",
  "conversion-web",
  "marketing-general",
];

export function GlossaryOverviewPage({ locale }: Props) {
  const isEn = locale === "en";
  const all = listGlossary(locale);
  const [activeCat, setActiveCat] = useState<GlossaryCategory | null>(null);
  const filtered = activeCat ? all.filter((e) => e.category === activeCat) : all;
  const grouped = groupByFirstLetter(filtered, locale);

  const tr = isEn
    ? {
        eyebrow: "Glossary",
        headlinePre: "Substance, not ",
        headlineEm: "Wikipedia copies.",
        lead: "Defined terms from performance marketing, SEO, AI SEO, conversion optimization — each with a standard definition and a Calvarius practice block. Pillar entries link out to in-depth services and articles.",
        meta: ["Editorial", "Independent", "From mandate practice"],
        kicker: "Inventory",
        sectionHead: "Index",
        ctaHead: "Want a mandate-grade conversation, not generic advice?",
        ctaBody:
          "If you're looking for substance on a specific performance, SEO or stack question — let's talk. We answer with the same clarity we write with.",
        ctaPrimary: "Start a conversation",
        ctaSecondary: "All services",
        breadHome: "Start",
        all: "All",
        pillarBadge: "Pillar entry",
        readEntry: "Read full entry →",
        deepenBlog: "Deepened in:",
        empty: "No entries in this category yet.",
      }
    : {
        eyebrow: "Glossar",
        headlinePre: "Substanz, keine ",
        headlineEm: "Wikipedia-Kopien.",
        lead: "Begriffs-Definitionen aus Performance Marketing, SEO, AI-SEO und Conversion-Optimierung — jeweils mit Standard-Definition und Calvarius-Praxis-Block. Pillar-Einträge verlinken in vertiefte Leistungen und Beiträge.",
        meta: ["Editorial", "Unabhängig", "Aus der Mandate-Praxis"],
        kicker: "Inventar",
        sectionHead: "Verzeichnis",
        ctaHead: "Substanz statt Generika — sprechen wir?",
        ctaBody:
          "Wenn Sie zu einer konkreten Performance-, SEO- oder Stack-Frage Substanz suchen — sprechen wir. Wir antworten mit der gleichen Klarheit, mit der wir schreiben.",
        ctaPrimary: "Erst-Gespräch anfragen",
        ctaSecondary: "Alle Leistungen",
        breadHome: "Start",
        all: "Alle",
        pillarBadge: "Pillar-Eintrag",
        readEntry: "Volltext lesen →",
        deepenBlog: "Vertieft in:",
        empty: "Noch keine Einträge in dieser Kategorie.",
      };

  // JSON-LD: DefinedTermSet + every visible DefinedTerm in one @graph.
  const setUrl = `${SITE_URL}${isEn ? pathMap.glossary.en : pathMap.glossary.de}`;
  const definedTermSet = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "DefinedTermSet",
        "@id": setUrl,
        name: isEn ? "Calvarius Glossary" : "Calvarius Glossar",
        description: tr.lead,
        url: setUrl,
        hasDefinedTerm: all.map((e) => ({
          "@id": entryCanonicalId(e, locale, isEn),
        })),
      },
      ...all.map((e) => ({
        "@type": "DefinedTerm",
        "@id": entryCanonicalId(e, locale, isEn),
        name: e.term[locale],
        description: e.shortDefinition[locale],
        termCode: e.id,
        inDefinedTermSet: setUrl,
        url: entryCanonicalId(e, locale, isEn),
      })),
    ],
  };

  const letters = [...grouped.keys()];

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={tr.eyebrow}
          headline={
            <>
              {tr.headlinePre}
              <em>{tr.headlineEm}</em>
            </>
          }
          lead={tr.lead}
          metaStrip={tr.meta}
          breadcrumbs={[
            { label: tr.breadHome, to: isEn ? "/en" : "/" },
            { label: tr.eyebrow },
          ]}
        />

        <Section number="02" kicker={tr.kicker} headline={<em>{tr.sectionHead}.</em>}>
          {/* Category filter pills */}
          <div className="mb-8 flex flex-wrap gap-2">
            <FilterPill
              active={activeCat === null}
              onClick={() => setActiveCat(null)}
              label={tr.all}
            />
            {ALL_CATEGORIES.map((cat) => (
              <FilterPill
                key={cat}
                active={activeCat === cat}
                onClick={() => setActiveCat(cat)}
                label={GLOSSARY_CATEGORY_LABELS[cat][locale]}
              />
            ))}
          </div>

          {/* A–Z anchor strip */}
          {letters.length > 0 && (
            <div className="mb-12 flex flex-wrap gap-1.5 border-b border-rule pb-6">
              {letters.map((l) => (
                <a
                  key={l}
                  href={`#letter-${l}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded font-mono text-xs font-medium text-ink-3 transition-colors hover:bg-bg-subtle hover:text-cyan-deep"
                >
                  {l}
                </a>
              ))}
            </div>
          )}

          {filtered.length === 0 ? (
            <p className="text-ink-3">{tr.empty}</p>
          ) : (
            <div className="space-y-12">
              {[...grouped.entries()].map(([letter, entries], gi) => (
                <RevealOnScroll key={letter} delay={gi * 30}>
                  <div id={`letter-${letter}`}>
                    <h2 className="mb-6 font-mono text-xs uppercase tracking-[0.18em] text-ink-4">
                      {letter}
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2">
                      {entries.map((e) => (
                        <EntryCard key={e.id} entry={e} locale={locale} tr={tr} />
                      ))}
                    </div>
                  </div>
                </RevealOnScroll>
              ))}
            </div>
          )}
        </Section>

        <Section number="03" variant="deep">
          <CTABlock
            variant="deep"
            headline={<em>{tr.ctaHead}</em>}
            body={tr.ctaBody}
            primary={{ label: tr.ctaPrimary, to: isEn ? pathMap.contact.en : pathMap.contact.de }}
            secondary={{ label: tr.ctaSecondary, to: isEn ? pathMap.services.en : pathMap.services.de }}
          />
        </Section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(definedTermSet) }}
      />
    </>
  );
}

function entryCanonicalId(e: GlossaryEntry, locale: Locale, isEn: boolean): string {
  const base = isEn ? pathMap.glossary.en : pathMap.glossary.de;
  if (e.tier === 1) {
    return `${SITE_URL}${base}/${e.slug[locale]}`;
  }
  return `${SITE_URL}${base}#${e.id}`;
}

function FilterPill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-2 font-mono text-xs uppercase tracking-wider transition-colors ${
        active
          ? "bg-ink text-white"
          : "border border-rule bg-white text-ink-3 hover:border-cyan-deep/40 hover:text-cyan-deep"
      }`}
    >
      {label}
    </button>
  );
}

function EntryCard({
  entry,
  locale,
  tr,
}: {
  entry: GlossaryEntry;
  locale: Locale;
  tr: { pillarBadge: string; readEntry: string; deepenBlog: string };
}) {
  const isEn = locale === "en";
  const detailHref = entry.tier === 1
    ? `${isEn ? pathMap.glossary.en : pathMap.glossary.de}/${entry.slug[locale]}`
    : null;

  // Pull first cross-link blogPost as deepening hint (Tier 2 only).
  const blogKey = entry.tier === 2 ? entry.crossLinks?.blogPosts?.[0] : undefined;
  const blogPath = blogKey ? pathMap[blogKey]?.[locale] : undefined;

  return (
    <article
      id={entry.id}
      className="flex flex-col rounded-lg border border-rule bg-white p-6 transition-colors hover:border-cyan-deep/40"
    >
      <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-4">
        <span>{GLOSSARY_CATEGORY_LABELS[entry.category][locale]}</span>
        {entry.tier === 1 && (
          <span className="rounded bg-lime/30 px-1.5 py-0.5 text-ink">{tr.pillarBadge}</span>
        )}
      </div>
      <h3 className="font-display text-xl leading-tight text-ink">
        {detailHref ? (
          <Link to={detailHref} className="hover:text-cyan-deep">
            {entry.term[locale]}
          </Link>
        ) : (
          entry.term[locale]
        )}
      </h3>
      <p className="mt-3 text-[15px] leading-relaxed text-ink-3">
        {entry.shortDefinition[locale]}
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-wider">
        {detailHref ? (
          <Link to={detailHref} className="text-cyan-deep hover:underline">
            {tr.readEntry}
          </Link>
        ) : blogPath ? (
          <Link to={blogPath} className="text-cyan-deep hover:underline">
            {tr.deepenBlog} →
          </Link>
        ) : (
          <span />
        )}
      </div>
    </article>
  );
}
